<template>
  <div class="mc-service-cost">
    <div class="d-flex mc-list-header">
      <h4 class="card-title flex-grow-1">{{ $t('invoice.title.serviceCost') }}</h4>
      <McButton
        v-if="hasSalesOrder"
        variant="primary"
        class="ms-auto"
        :text="$t('global.addField')"
        icon="icon-fill_plus"
        @click="handleAdd"
      />
    </div>
    <b-card class="mc-service-cost__card">
      <FormulateForm
        v-model="formValues"
        name="editable-table-form"
        ref="formRef"
        :errors="formErrors"
        @submit="handleSubmit"
        @failed-validation="handleFailedValidation"
        v-if="hasSalesOrder"
      >
        <div class="table-responsive border border-1 mc-service-cost__editable-table">
          <b-table
            :busy="isBusy"
            :items="items"
            :fields="computedFields"
            fixed
            show-empty
            class="editable-table"
          >
            <!-- Loading -->
            <template #table-busy>
              <div class="__busy">
                <b-spinner class="align-middle mb-2"></b-spinner>
                <strong>{{ $t('global.text.loading') }}...</strong>
              </div>
            </template>

            <!-- Empty Result -->
            <template #empty>
              <div class="text-center my-4">{{ $t('global.text.noData') }}</div>
            </template>

            <!-- Slot: Header Data -->
            <template #head()="data">
              <div class="d-flex">
                {{ data.label }}
                <template v-if="data.field.isSortable">
                  <div>
                    <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                      <i class="mc-icon icon-outline_chevron-up"></i>
                    </div>
                    <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                      <i class="mc-icon icon-outline_chevron-down"></i>
                    </div>
                  </div>
                </template>
              </div>
            </template>

            <!-- Slot: All Cells -->
            <template #cell()="{ index, field, value, item }">
              <!-- Editable, <Edit action> -->
              <template v-if="field.isEditable && isEditingRow == index">
                <FormulateInput
                  v-if="field.name === 'id'"
                  v-bind="{ ...field }"
                  :rowItem="item"
                  :label="null"
                  :name="field.name"
                  :type="field.type || 'text'"
                  :fullObject.sync="serviceObj"
                  :disabled="isSubmitting"
                  class="p-0"
                  validation-behavior="live"
                  :validation-name="field.label"
                  error-behavior="value"
                  size="sm"
                  :ref="`${field.name}-ref`"
                />
                <div v-else-if="field.name === 'delivery_order_id'">
                  <CustomValueHelp
                    v-bind="{ ...field }" 
                    @updateOrderValue="updateOrderValue" 
                    :fullObject.sync="selectedOrder"
                  />
                </div>
                <FormulateInput
                  v-else
                  v-bind="{ ...field }"
                  :rowItem="item"
                  :label="null"
                  :name="field.name"
                  :type="field.type || 'text'"
                  :disabled="isSubmitting"
                  class="p-0"
                  validation-behavior="live"
                  :validation-name="field.label"
                  error-behavior="value"
                  size="sm"
                  :ref="`${field.name}-ref`"
                />
              </template>

              <!-- Not Editable, Show Only Column, <Edit action> -->
              <template v-else-if="!field.isEditable && isEditingRow == index">
                <div v-if="field.name === 'total_price'">{{ transformCellValue(totalPrice) }}</div>
                <div v-else> {{ showOnlyValue(value) }} </div>
              </template>

              <!-- Nothing happen action, the page is first loaded -->
              <template v-else>
                <div v-if="field.name === 'total_price'">
                  {{ transformCellValue((item.quantity * item.unit_price) - item.discount, field) }}
                </div>
                <div v-else-if="field.name === 'sales_order_number'">-</div>
                <div v-else>
                  {{ transformCellValue(value, field) }}
                </div>
              </template>
            </template>

            <!-- Slot: Action Column (Last Column) -->
            <template #cell(action)="data">
              <div v-if="isEditingRow == data.index">
                <b-button
                  :class="{
                    __disabled: $refs.formRef?.hasErrors,
                  }"
                  variant="outline"
                  class="__accept-button btn"
                  :type="$refs.formRef?.hasErrors ? 'button' : 'submit'"
                >
                  <i class="mc-icon icon-fill_checkmark"></i>
                </b-button>

                <b-button
                  variant="outline"
                  class="__cancel-button btn"
                  @click="handleEditCancel()"
                >
                  <i class="mc-icon icon-outline_close"></i>
                </b-button>
              </div>

              <b-button
                v-else
                variant="outline"
                :class="{
                  '__edit-button btn': true,
                  __disabled: isEditingRow !== null || isBusy,
                }"
                @click="handleEditStart(data)"
              >
                <i class="mc-icon icon-outline_edit"></i>
              </b-button>
            </template>
          </b-table>
        </div>
        <b-row align-h="end" class="mc-service-cost__total">
          <b-col cols="12" sm="3">
            <b-row>
              <b-col cols="12" sm="6" class="pe-0">
                <div class="__label">{{ $t('invoice.fields.totalBill') }}</div>
              </b-col>
              <b-col cols="12" sm="6" class="ps-0">
                <div class="__value">{{ totalAmount }}</div>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </FormulateForm>
      <div v-else class="empty-state">
        <img src="@/assets/images/no-data-illustration.png" width="250" alt />
        <div class="empty-state-message">
          <strong>{{ $t('invoice.title.noSalesOrder') }}</strong>
          <p>{{ $t('invoice.subtitle.noSalesOrder') }}</p>
      </div>
      </div>
    </b-card>

  </div>
</template>
<script>
import { sum } from '@/helpers/calculate'
import McButton from '@/components/McButton'
import CustomValueHelp from '../CustomValueHelp'
import serviceCostFields from '../../lib/fields/serviceCostFields'
import { mapState } from 'vuex'
import isEmpty from 'lodash/isEmpty'
export default {
  components: {
    McButton,
    CustomValueHelp
  },
  mixins: [serviceCostFields],
  props:{
    hasSalesOrder: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      items: [],
      isBusy: false,
      isAdding: false,
      formValues: {},
      formErrors: {},
      serviceObj: {},
      isEditingRow: null,
      isSubmitting: false,
      selectedOrder: {}
    }
  },
  computed: {
    ...mapState('invoice', ['products']),
    computedFields() {
      let fields = [
        ...this.serviceCostFields.filter(v => v.isShow === true),
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
          thStyle: 'width: 70px'
        },
      ]
      
      return fields
    },
    totalAmount() {
      let totalAmount = 0
      this.items.forEach((item) => {
        const total = (item.quantity * item.unit_price) - item.discount
        if (isNaN(total)) return
        totalAmount = sum(totalAmount ?? 0, Number(total))
      })

      return totalAmount.toLocaleString() ?? '-'
    },
    totalPrice() {
      const total = (this.formValues?.quantity * this.formValues?.unit_price) - this.formValues?.discount
      return isNaN(total) ? 0 : total
    }
  },
  watch: {
    serviceObj: function (newVal) {
      if (this.isAdding ) {
        this.formValues['unit_price'] = newVal.price
        this.formValues['discount'] = newVal.discount_amount
        this.formValues['quantity'] = 1
        if (!isEmpty(this.selectedOrder)) {
          this.formValues['delivery_order_id'] = this.selectedOrder.id
        }
        this.formValues['description'] = newVal.description
      }
    }
  },
  mounted() {
    this.items = this.products
  },
  methods: {
    handleAdd() {
      if (this.isEditingRow !== null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.items = [{ id: 0 }, ...this.items]
    },
    handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning(this.$t('global.alert.text.pleaseWait'))
        return
      }

      if (Object.keys(this.formErrors).length) {
        this.$mcToast.error(this.$t('setting.wrongFormat'))
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.serviceCostFields.filter(v => v.isShow === true))

      this.$delete(transformedData, 'id')
      this.$delete(transformedData, 'delivery_order_id')
      this.$set(transformedData, 'product', this.serviceObj)
      this.$set(transformedData, 'delivery_order', this.selectedOrder)

      if (transformedData) {
        this.items.splice(this.isEditingRow, 1, transformedData)
      }

      this.$store.commit('invoice/setProducts', this.items)

      this.serviceObj = {}
      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
      this.selectedOrder = {}
      delete this.formValues['delivery_order_id']
    },
    async handleEditStart(data) {
      if (this.isEditingRow !== null) return

      const item = this.items[data.index]
      this.computedFields.forEach(field => {
        if (!field.isEditable) return
        let value

        // assign for the product
        if (field.name === 'id') value = item.product.id
        else if (field.name === 'delivery_order_id') value = item.delivery_order.id
        else value = item[field.name]
        this.$set(this.formValues, field.name, value)
      })

      this.serviceObj = { ...item.product }
      this.selectedOrder = { ...item.delivery_order}
      this.isEditingRow = data.index
    },
    handleEditCancel() {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
        delete this.formValues['delivery_order_id']
        this.selectedOrder = {}
      }

      this.formErrors = {}
      this.serviceObj = {}
      this.isEditingRow = null
    },
    handleFailedValidation() {
      return this.$mcToast.error(this.$t('setting.wrongFormat'))
    },
    showOnlyValue(value) {
      return value || '-'
    },
    transformCellValue(value) {
      if (value === false) return `-`
      if (typeof value === 'number') return `${value.toLocaleString()}`
      return value
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: '' }
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    updateOrderValue(order) {
      this.formValues['delivery_order_id'] = order.id
    }
  }
}
</script>
<style lang="scss">
  @import './style.scss';
</style>